"use client";

import { useEffect, useRef, useState } from "react";
import { ArrowLeft, Eye, Save } from "lucide-react";
import { Button, CheckList, Field, FieldGrid, FormSection, Input, SelectField, Textarea } from ".";
import { Checkbox } from "../ui/checkbox";
import { Sheet, SheetContent, SheetDescription, SheetHeader, SheetTitle } from "../ui/sheet";
import { useUnsavedWork } from "./unsaved-work";
import {
  type ChannelOption,
  CONFIG,
  paragraphs,
  subtree,
  writableOrgs,
  type ContentKind,
  type ContentRecord,
  type OrgNode
} from "./content-model";
import { ContentPreview } from "./content-preview";
import type { WhoAmI } from "../../lib/session";
import { listPortalChannels, portalError } from "../../lib/portal-pipeline-api";
import { api, formatTime } from "../../lib/api";

type SourceActivity = {
  publicRef: string;
  title: string;
  startAt: string;
  endAt: string;
  status: "DRAFT" | "PUBLISHED" | "CANCELLED";
};

function localDate() {
  const date = new Date();
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;
}
export function ContentEditor({
  kind,
  record,
  orgs,
  orgError,
  reloadOrgs,
  whoami,
  working,
  failure,
  onSave,
  onLeave,
  onReload
}: {
  kind: ContentKind;
  record: ContentRecord | null;
  orgs: OrgNode[];
  orgError: string | null;
  reloadOrgs: () => void;
  whoami: WhoAmI | null;
  working: boolean;
  failure: string | null;
  onSave: (body: Record<string, unknown>) => Promise<void>;
  onLeave: () => void;
  onReload: () => void;
}) {
  const portal = kind === "portal";
  const options = writableOrgs(orgs, whoami);
  const [channels, setChannels] = useState<ChannelOption[]>([]);
  const [channelError, setChannelError] = useState("");
  const [channelReload, setChannelReload] = useState(0);
  useEffect(() => {
    if (!portal) return;
    let current = true;
    setChannels([]); setChannelError("");
    void listPortalChannels().then(result => {
      if (current) setChannels(result.items.map(row => ({ value: row.channelKey, label: row.title })));
    }).catch(error => { if (current) setChannelError(portalError(error)); });
    return () => { current = false; };
  }, [portal, channelReload]);
  const [form, setForm] = useState(() => ({
    organizationId: record?.organization.id ?? (options.length === 1 ? options[0]!.id : ""),
    title: record?.title ?? "",
    body: Array.isArray(record?.body) ? record.body.join("\n\n") : (record?.body ?? ""),
    category: record?.category ?? "会议通知",
    publisherLabel: record?.publisherLabel ?? "市委会办公室",
    sourceType: record?.sourceType ?? "会议",
    sourceRef: record?.sourceRef ?? "",
    audienceMode: record?.audienceMode ?? "ORGANIZATION_SUBTREE",
    audienceOrganizationIds:
      record?.audienceOrganizationIds ?? (options.length === 1 ? [options[0]!.id] : []),
    requiresAcknowledgement: record?.requiresAcknowledgement ?? false,
    subjectConsentRef: record?.subjectConsentRef ?? "",
    channelKey: record?.channelKey ?? "NEWS",
    genre: record?.genre ?? "",
    digest: record?.digest ?? "",
    displayDate: record?.displayDate ?? localDate(),
    themes: (record?.themes ?? []).join("，"),
    publicSource: record?.publicSource ?? ""
  }));
  const initial = useRef(JSON.stringify(form));
  const dirty = JSON.stringify(form) !== initial.current;
  const { requestLeave, dialog } = useUnsavedWork(dirty, working);
  const [preview, setPreview] = useState(false);
  const [validation, setValidation] = useState<string | null>(null);
  const [activities, setActivities] = useState<SourceActivity[]>([]);
  const [activitiesLoading, setActivitiesLoading] = useState(!portal);
  const [activitiesFailed, setActivitiesFailed] = useState(false);
  const [activityReload, setActivityReload] = useState(0);
  const [activitySearch, setActivitySearch] = useState("");
  const [linkedActivity, setLinkedActivity] = useState<SourceActivity | null>(null);
  const sourceTouched = useRef(false);
  useEffect(() => {
    if (portal) return;
    let current = true;
    void api<{ items: SourceActivity[] }>("/api/v1/activities")
      .then((result) => {
        if (!current) return;
        setActivities(result.items);
        // 已有来源只按编号识别；回显不改写草稿，也不能覆盖读取期间的手填。
        if (!sourceTouched.current && record?.sourceRef) {
          setLinkedActivity(result.items.find((row) => row.publicRef === record.sourceRef) ?? null);
        }
      })
      .catch(() => {
        if (current) setActivitiesFailed(true);
      })
      .finally(() => {
        if (current) setActivitiesLoading(false);
      });
    return () => { current = false; };
  }, [portal, record?.sourceRef, activityReload]);
  const availableActivities = activities
    .filter((row) => row.status !== "CANCELLED" && new Date(row.endAt).getTime() >= Date.now())
    .sort((a, b) => new Date(a.startAt).getTime() - new Date(b.startAt).getTime());
  const matchingActivities = availableActivities.filter((row) =>
    row.title.toLocaleLowerCase().includes(activitySearch.trim().toLocaleLowerCase())
  );
  const errorRef = useRef<HTMLDivElement>(null);
  const field = <K extends keyof typeof form>(key: K, value: (typeof form)[K]) =>
    setForm((prev) => ({ ...prev, [key]: value }));
  const allowed = new Set(options.map((node) => node.id));
  const targets = subtree(orgs, form.organizationId).filter((node) => allowed.has(node.id));
  const audienceValid =
    form.audienceOrganizationIds.length > 0 &&
    form.audienceOrganizationIds.every((id) => targets.some((node) => node.id === id));
  const canSave =
    !working && !orgError && allowed.has(form.organizationId) && (portal ? !channelError && channels.some(row => row.value === form.channelKey) : audienceValid);
  const themeList = [...new Set(form.themes.split(/[,，\s]+/).filter(Boolean))];
  const previewRecord: Partial<ContentRecord> = {
    ...record,
    ...form,
    body: portal ? paragraphs(form.body) : form.body,
    channelTitle:
      channels.find((row) => row.value === form.channelKey)?.label ?? record?.channelTitle,
    themes: themeList
  };
  const save = async (event: React.FormEvent) => {
    event.preventDefault();
    if (!canSave) return;
    const body = paragraphs(form.body);
    let problem: string | null = null;
    if (!form.title.trim() || !form.body.trim()) problem = "请填写标题和正文，不能只包含空格。";
    if (!portal && (form.title.trim().length < 4 || form.body.trim().length < 8))
      problem = "通知标题至少 4 个字，正文至少 8 个字。";
    if (portal && (!form.digest.trim() || body.length > 60 || body.some((p) => p.length > 4000)))
      problem = "请填写摘要；正文最多 60 段，每段不超过 4000 字。";
    if (portal && (themeList.length > 12 || themeList.some((p) => p.length > 40)))
      problem = "主题词最多 12 个，每个不超过 40 字。";
    if (portal && form.channelKey === "FIGURES" && !form.subjectConsentRef.trim()) problem = "会员风采须填写本人同意依据。";
    setValidation(problem);
    if (problem) {
      requestAnimationFrame(() => errorRef.current?.focus());
      return;
    }
    const content = portal
      ? {
          subjectConsentRef: form.subjectConsentRef.trim(),
          channelKey: form.channelKey,
          genre: form.genre.trim(),
          title: form.title.trim(),
          digest: form.digest.trim(),
          body,
          displayDate: form.displayDate,
          themes: themeList,
          publicSource: form.publicSource.trim()
        }
      : {
          category: form.category.trim(),
          title: form.title.trim(),
          body: form.body.trim(),
          publisherLabel: form.publisherLabel.trim(),
          sourceType: form.sourceType.trim(),
          sourceRef: form.sourceRef.trim(),
          audienceMode: form.audienceMode,
          audienceOrganizationIds: form.audienceOrganizationIds,
          requiresAcknowledgement: form.requiresAcknowledgement
        };
    await onSave({
      ...content,
      ...(record ? { expectedVersion: record.version } : { organizationId: form.organizationId })
    });
  };
  const sections = portal
    ? ["稿件正文", "发布设置", "公开边界"]
    : ["通知正文", "接收范围", "来源与署名"];
  return (
    <>
      {dialog}
      <div className="workspace-heading">
        <div>
          <button className="content-back" disabled={working} onClick={() => requestLeave(onLeave)}>
            <ArrowLeft size={16} />
            返回{CONFIG[kind].title}
          </button>
          <h1 className="workspace-title">
            {record ? "编辑" : "新建"}
            {CONFIG[kind].noun}
          </h1>
          <p className="workspace-subtitle">
            {record
              ? `正在编辑已保存草稿 · 版本 ${record.version}`
              : "先把内容写清楚，再核对范围和下一步。"}
          </p>
        </div>
        <Button variant="outline" type="button" onClick={() => setPreview(true)}>
          <Eye size={16} />
          阅读预览
        </Button>
      </div>
      <div className="activity-editor content-editor">
        <nav className="activity-editor__nav" aria-label="编辑章节">
          <p>本篇设置</p>
          {sections.map((title, i) => (
            <a href={`#content-section-${i}`} key={title}>
              <span>0{i + 1}</span>
              {title}
            </a>
          ))}
          <div className="content-editor__boundary">
            <LockCopy portal={portal} />
          </div>
        </nav>
        <form className="activity-editor__form" onSubmit={(event) => void save(event)}>
          <fieldset disabled={working} className="content-editor__fields">
            <FormSection
              id="content-section-0"
              step="01"
              title={sections[0]!}
              description={
                portal ? "摘要用于列表引读，正文按空行分段。" : "说明要办的事、时间安排与具体要求。"
              }
            >
              <FieldGrid>
                <Field label="标题" htmlFor="content-title" wide>
                  <Input
                    id="content-title"
                    value={form.title}
                    onChange={(e) => field("title", e.target.value)}
                    required
                    maxLength={120}
                    minLength={portal ? 1 : 4}
                    placeholder={
                      portal ? "给这篇内容一个清楚的标题" : "例如：关于开展专题调研的通知"
                    }
                  />
                </Field>
                {portal && (
                  <Field
                    label="摘要"
                    htmlFor="content-digest"
                    hint={`${form.digest.length} / 300 字`}
                    wide
                  >
                    <Textarea
                      id="content-digest"
                      value={form.digest}
                      onChange={(e) => field("digest", e.target.value)}
                      rows={3}
                      maxLength={300}
                      required
                      placeholder="用一小段话概括主要内容"
                    />
                  </Field>
                )}
                <Field
                  label="正文"
                  htmlFor="content-body"
                  hint={
                    portal
                      ? `${paragraphs(form.body).length} 段 · 空行分段，每段最多 4000 字`
                      : `${form.body.length} / 8000 字`
                  }
                  wide
                >
                  <Textarea
                    id="content-body"
                    className="content-editor__body"
                    value={form.body}
                    onChange={(e) => field("body", e.target.value)}
                    rows={12}
                    maxLength={portal ? 240120 : 8000}
                    required
                    minLength={portal ? 1 : 8}
                    placeholder="在这里开始写作…"
                  />
                </Field>
              </FieldGrid>
            </FormSection>
            <FormSection
              id="content-section-1"
              step="02"
              title={sections[1]!}
              description={
                portal
                  ? "展示日期用于文章署名区，不是定时发布设置。"
                  : "按在册会籍确定接收人；人数在保存后预估，放行时确定。"
              }
            >
              {orgError && (
                <div className="workspace-error" role="alert">
                  组织读取失败：{orgError}
                  <Button type="button" variant="outline" onClick={reloadOrgs}>
                    重新读取组织
                  </Button>
                </div>
              )}
              <FieldGrid>
                <Field
                  label="承办组织"
                  hint={record ? "已保存稿件的承办组织不可更换" : "来自你的内容起草授权范围"}
                >
                  <SelectField
                    value={form.organizationId}
                    onChange={(id) =>
                      setForm((prev) => ({
                        ...prev,
                        organizationId: id,
                        audienceOrganizationIds: []
                      }))
                    }
                    disabled={!!record}
                    placeholder="请选择组织"
                    options={options.map((node) => ({ value: node.id, label: node.name }))}
                  />
                </Field>
                {portal ? (
                  <>
                    {channelError && <p role="alert">{channelError}<Button type="button" variant="outline" onClick={() => setChannelReload(n => n + 1)}>重新读取栏目</Button></p>}
                    <Field label="栏目">
                      <SelectField
                        value={form.channelKey}
                        onChange={(value) => field("channelKey", value)}
                        options={channels}
                        placeholder="请选择栏目"
                      />
                    </Field>
                    {form.channelKey === "FIGURES" && <Field label="本人同意依据" hint="请填写同意记录的依据编号；合成内容使用合成依据。"><Input aria-required="true" value={form.subjectConsentRef} maxLength={240} onChange={e => field("subjectConsentRef", e.target.value)} /></Field>}
                    <Field label="展示日期" htmlFor="content-date">
                      <Input
                        id="content-date"
                        type="date"
                        value={form.displayDate}
                        onChange={(e) => field("displayDate", e.target.value)}
                        required
                      />
                    </Field>
                    <Field label="体裁" htmlFor="content-genre" hint="选填">
                      <Input
                        id="content-genre"
                        value={form.genre}
                        onChange={(e) => field("genre", e.target.value)}
                        maxLength={40}
                      />
                    </Field>
                    <Field
                      label="主题词"
                      htmlFor="content-themes"
                      hint="选填，逗号分隔，最多 12 个"
                      wide
                    >
                      <Input
                        id="content-themes"
                        value={form.themes}
                        onChange={(e) => field("themes", e.target.value)}
                      />
                    </Field>
                  </>
                ) : (
                  <Field label="范围方式">
                    <SelectField
                      placeholder="请选择范围方式"
                      value={form.audienceMode}
                      onChange={(value) => field("audienceMode", value as typeof form.audienceMode)}
                      options={[
                        { value: "ORGANIZATION_SUBTREE", label: "所选组织及其下级" },
                        { value: "EXPLICIT_ORGANIZATIONS", label: "仅所选组织本级" }
                      ]}
                    />
                  </Field>
                )}
              </FieldGrid>
              {!portal && (
                <div className="mt-4">
                  <h3 className="content-field-title">接收组织</h3>
                  <CheckList
                    options={targets.map((node) => ({ id: node.id, label: node.name }))}
                    value={form.audienceOrganizationIds}
                    onChange={(value) => field("audienceOrganizationIds", value)}
                    emptyText="请先选择承办组织，并确认组织已读取成功。"
                    quickActions={
                      <Button
                        type="button"
                        size="sm"
                        variant="ghost"
                        onClick={() => field("audienceOrganizationIds", [])}
                      >
                        清空
                      </Button>
                    }
                  />
                  {!audienceValid && (
                    <p className="content-field-note">请至少选择一个授权范围内的接收组织。</p>
                  )}
                  <label className="content-check">
                    <Checkbox
                      checked={form.requiresAcknowledgement}
                      onCheckedChange={(value) => field("requiresAcknowledgement", value === true)}
                    />
                    <span>
                      需要接收人逐一确认知悉<small>阅读通知和确认知悉是两件事。</small>
                    </span>
                  </label>
                </div>
              )}
            </FormSection>
            <FormSection id="content-section-2" step="03" title={sections[2]!}>
              {portal ? (
                <>
                  <Field
                    label="公开来源"
                    htmlFor="content-source"
                    hint="选填，只填写适合公众阅读的来源名称"
                  >
                    <Input
                      id="content-source"
                      value={form.publicSource}
                      onChange={(e) => field("publicSource", e.target.value)}
                      maxLength={120}
                    />
                  </Field>
                  <div className="content-boundary-note">
                    <strong>正文将面向公众</strong>
                    <p>
                      请核对姓名、联系方式、企业经营信息及内部材料的公开边界。保存草稿后可添加内部审查附件，再送保密与公开性审查。
                    </p>
                  </div>
                </>
              ) : (
                <>
                  <div className="content-activity-source" role="group" aria-label="关联活动（可选）">
                    <strong>关联活动（可选）</strong>
                    {linkedActivity ? (
                      <div className="workspace-actions">
                        <span role="status">已关联：{linkedActivity.title}</span>
                        <Button type="button" variant="ghost" size="sm" onClick={() => {
                          sourceTouched.current = true;
                          setLinkedActivity(null);
                          field("sourceRef", "");
                        }}>取消关联</Button>
                      </div>
                    ) : activitiesLoading ? (
                      <p role="status">正在读取活动列表…仍可手填来源。</p>
                    ) : !activitiesFailed ? (
                      <>
                        <Field label="按标题筛选活动" htmlFor="content-activity-search">
                          <Input id="content-activity-search" value={activitySearch}
                            onChange={(event) => setActivitySearch(event.target.value)} />
                        </Field>
                        <p className="workspace-hint">本次最多读取 100 场授权范围内活动，仅列出未结束且未取消的活动，按开始时间排列；搜索仅限本次列表。</p>
                        <div className="content-activity-source__list" aria-label="可关联活动">
                          {matchingActivities.map((activity) => (
                            <Button type="button" variant="ghost" key={activity.publicRef}
                              className="content-activity-source__option" onClick={() => {
                                sourceTouched.current = true;
                                setLinkedActivity(activity);
                                setForm((previous) => ({ ...previous, sourceType: "活动", sourceRef: activity.publicRef }));
                              }}>
                              <span>{activity.title}</span>
                              <span>{formatTime(activity.startAt)} · {activity.publicRef}</span>
                            </Button>
                          ))}
                          {!matchingActivities.length && <p>{activitySearch.trim() ? "本次列表没有匹配的活动。" : "本次列表没有可关联的未结束活动。"}</p>}
                        </div>
                      </>
                    ) : null}
                    {activitiesFailed && (
                      <div role="status">
                        <p>活动列表暂未读取到，仍可手填来源</p>
                        <Button type="button" variant="outline" size="sm" onClick={() => {
                          setActivitiesLoading(true);
                          setActivitiesFailed(false);
                          setActivityReload((previous) => previous + 1);
                        }}>重试读取活动</Button>
                      </div>
                    )}
                  </div>
                  <FieldGrid>
                  <Field label="发布署名" htmlFor="content-publisher">
                    <Input
                      id="content-publisher"
                      value={form.publisherLabel}
                      onChange={(e) => field("publisherLabel", e.target.value)}
                      maxLength={60}
                      required
                    />
                  </Field>
                  <Field label="类别" htmlFor="content-category">
                    <Input
                      id="content-category"
                      value={form.category}
                      onChange={(e) => field("category", e.target.value)}
                      maxLength={40}
                      required
                    />
                  </Field>
                  <Field label="来源类型" htmlFor="content-source-type">
                    <Input
                      id="content-source-type"
                      value={form.sourceType}
                      readOnly={!!linkedActivity}
                      onChange={(e) => { sourceTouched.current = true; field("sourceType", e.target.value); }}
                      maxLength={40}
                      required
                    />
                  </Field>
                  <Field
                    label="来源编号"
                    htmlFor="content-source-ref"
                    hint="内部追溯使用，不在阅读预览中展示"
                  >
                    <Input
                      id="content-source-ref"
                      value={form.sourceRef}
                      readOnly={!!linkedActivity}
                      onChange={(e) => { sourceTouched.current = true; field("sourceRef", e.target.value); }}
                      maxLength={80}
                      required
                    />
                  </Field>
                  </FieldGrid>
                </>
              )}
            </FormSection>
          </fieldset>
          {(validation || failure) && (
            <div ref={errorRef} tabIndex={-1} className="workspace-error" role="alert">
              {validation || failure}
              {failure && record && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={working}
                  onClick={() => requestLeave(onReload)}
                >
                  重新读取已保存版本
                </Button>
              )}
            </div>
          )}
          <footer className="activity-editor__footer">
            <p>
              <strong>
                {working
                  ? "正在保存…"
                  : dirty
                    ? "有未保存的内容"
                    : record
                      ? "与已保存版本一致"
                      : "尚未保存"}
              </strong>
              <br />
              保存草稿后可核对并送审。
            </p>
            <div className="workspace-actions">
              <Button
                type="button"
                variant="ghost"
                disabled={working}
                onClick={() => requestLeave(onLeave)}
              >
                返回列表
              </Button>
              <Button type="submit" disabled={!canSave}>
                <Save size={16} />
                {working ? "正在保存…" : record ? "保存修改" : "存为草稿"}
              </Button>
            </div>
          </footer>
        </form>
      </div>
      <Sheet open={preview} onOpenChange={setPreview}>
        <SheetContent className="activity-sheet content-sheet workspace">
          <SheetHeader>
            <SheetTitle>阅读预览</SheetTitle>
            <SheetDescription>预览当前填写内容，尚未保存的修改也会显示。</SheetDescription>
          </SheetHeader>
          <div className="activity-sheet__body">
            <ContentPreview kind={kind} record={previewRecord} />
          </div>
          <footer className="content-sheet__footer">
            <Button variant="outline" onClick={() => setPreview(false)}>
              继续编辑
            </Button>
          </footer>
        </SheetContent>
      </Sheet>
    </>
  );
}
function LockCopy({ portal }: { portal: boolean }) {
  return (
    <p>
      {portal ? "草稿 → 保密审查 → 放行 → 公开" : "草稿 → 送审 → 放行发布"}
      <br />
      <span>保存不会直接发布。</span>
    </p>
  );
}
